import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, Download, Calendar, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function StaffSalary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [salaryData, setSalaryData] = useState(null);
  const [staffInfo, setStaffInfo] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      // Fetch salary and profile data
      const [salaryRes, profileRes] = await Promise.all([
        fetch(`${API_URL}/api/staff-portal/salary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/staff-portal/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (salaryRes.ok) {
        const data = await salaryRes.json();
        setSalaryData(data);
      }

      if (profileRes.ok) {
        const data = await profileRes.json();
        setStaffInfo(data.profile);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedSlip = () => {
    if (!salaryData?.payments) return null;
    return salaryData.payments.find(p => {
      const payDate = new Date(p.payment_date || p.month);
      return payDate.getMonth() + 1 === selectedMonth && payDate.getFullYear() === selectedYear;
    });
  };

  const downloadSalarySlip = () => {
    const slip = getSelectedSlip();
    const template = salaryData?.template;
    
    // Use slip data if available, otherwise use template or staff info
    const basicSalary = slip?.basic_salary || template?.basic_salary || staffInfo?.salary || 0;
    const hra = slip?.hra || template?.hra || 0;
    const da = slip?.da || template?.da || 0;
    const otherAllowances = slip?.other_allowances || template?.other_allowances || 0;
    const grossSalary = slip?.gross_salary || template?.gross_salary || basicSalary;
    const pfDeduction = slip?.pf_deduction || template?.pf_deduction || 0;
    const esiDeduction = slip?.esi_deduction || template?.esi_deduction || 0;
    const tds = slip?.tds || template?.tds || 0;
    const otherDeductions = slip?.other_deductions || template?.other_deductions || 0;
    const totalDeductions = slip?.total_deductions || template?.total_deductions || 0;
    const netSalary = slip?.net_salary || template?.net_salary || (grossSalary - totalDeductions);

    // Create printable content
    const content = `
      SALARY SLIP
      ====================
      Month: ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}
      
      Employee: ${staffInfo?.full_name || 'N/A'}
      Employee ID: ${staffInfo?.employee_id || 'N/A'}
      Department: ${staffInfo?.department || 'N/A'}
      Designation: ${staffInfo?.designation || 'N/A'}
      
      EARNINGS
      --------
      Basic Salary: ₹${basicSalary}
      HRA: ₹${hra}
      DA: ₹${da}
      Other Allowances: ₹${otherAllowances}
      
      Total Earnings: ₹${grossSalary}
      
      DEDUCTIONS
      ----------
      PF: ₹${pfDeduction}
      ESI: ₹${esiDeduction}
      TDS: ₹${tds}
      Other Deductions: ₹${otherDeductions}
      
      Total Deductions: ₹${totalDeductions}
      
      NET SALARY: ₹${netSalary}
      ====================
      ${!slip ? '\n      Note: This is a provisional salary slip based on your salary template.' : ''}
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Salary_${selectedMonth}_${selectedYear}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const selectedSlip = getSelectedSlip();
  const template = salaryData?.template;
  const baseSalary = selectedSlip?.basic_salary || template?.basic_salary || staffInfo?.salary || 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-salary-page">
      {/* Header */}
      <header className="bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/dashboard" className="p-2 hover:bg-indigo-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">My Salary</h1>
              <p className="text-indigo-200 text-sm">View and download salary slips</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Month/Year Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Month</label>
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-500 mb-1 block">Year</label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={downloadSalarySlip} className="bg-green-600 hover:bg-green-700">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Salary Slip Card */}
        <Card className="border-2 border-indigo-200">
          <CardHeader className="bg-indigo-50 border-b">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-indigo-600" />
                Salary Slip - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
              </div>
              {selectedSlip ? (
                <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded">Paid</span>
              ) : (
                <span className="text-sm bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Pending</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Employee Info */}
            <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b">
              <div>
                <p className="text-sm text-gray-500">Employee Name</p>
                <p className="font-medium">{staffInfo?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Employee ID</p>
                <p className="font-medium font-mono">{staffInfo?.employee_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-medium">{staffInfo?.department || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Designation</p>
                <p className="font-medium">{staffInfo?.designation || 'N/A'}</p>
              </div>
            </div>

            {/* Earnings & Deductions */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Earnings */}
              <div>
                <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Earnings
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Basic Salary</span>
                    <span className="font-medium">₹{baseSalary.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">HRA</span>
                    <span className="font-medium">₹{(selectedSlip?.hra || template?.hra || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">DA</span>
                    <span className="font-medium">₹{(selectedSlip?.da || template?.da || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Other Allowances</span>
                    <span className="font-medium">₹{(selectedSlip?.other_allowances || template?.other_allowances || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t font-semibold text-green-700">
                    <span>Total Earnings</span>
                    <span>₹{(selectedSlip?.gross_salary || baseSalary).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Deductions
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">PF</span>
                    <span className="font-medium">₹{(selectedSlip?.pf_deduction || template?.pf || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">ESI</span>
                    <span className="font-medium">₹{(selectedSlip?.esi_deduction || template?.esi || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">TDS</span>
                    <span className="font-medium">₹{(selectedSlip?.tds || template?.tds || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Other Deductions</span>
                    <span className="font-medium">₹{(selectedSlip?.other_deductions || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t font-semibold text-red-700">
                    <span>Total Deductions</span>
                    <span>₹{(selectedSlip?.total_deductions || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Salary */}
            <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-indigo-900">Net Salary</span>
                <span className="text-2xl font-bold text-indigo-700">
                  ₹{(selectedSlip?.net_salary || baseSalary).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {salaryData?.payments?.length > 0 ? (
              <div className="space-y-2">
                {salaryData.payments.slice(0, 6).map((payment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium">{payment.month_name || formatDate(payment.payment_date)}</p>
                        <p className="text-xs text-gray-500">Paid on {formatDate(payment.payment_date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">₹{(payment.net_salary || payment.amount).toLocaleString('en-IN')}</p>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Paid</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No payment history available</p>
                <p className="text-sm">Your salary records will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
